import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  Avatar,
  Button,
  Chip,
  Icon,
  IconButton,
  Menu,
  RoleChip,
  Segmented,
} from '@w7/ui'
import { isNavItemActive, type NavItem } from './nav'
import { useConsoleNav } from './useConsoleNav'
import { useSession } from './queries'
import { useDateRange, RANGE_PRESETS } from './range'
import { ROLE_LABEL } from '@/data/types'
import { isDemoSession, logout, claims } from '@/auth/oidc'
import { SearchTrigger } from '@/features/search'
import { StrixMark } from '@/features/auth/StrixMark'
import { useThemePrefs } from './theme'

const RAIL_COLLAPSED_KEY = 'w7.rail.collapsed'
const RAIL_OPEN_SECTIONS_KEY = 'w7.rail.sections'

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/** Below this the rail is a drawer, and a drawer always shows full labels. */
const RAIL_DRAWER_QUERY = '(max-width: 900px)'

function useIsRailDrawer(): boolean {
  const [drawer, setDrawer] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia(RAIL_DRAWER_QUERY)
    const sync = () => setDrawer(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return drawer
}

function readOpenSections(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(RAIL_OPEN_SECTIONS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

/**
 * The authenticated shell.
 *
 * Rail is viewport-locked (does not scroll with page content). The nav list
 * scrolls only when items overflow — typical on short viewports. Desktop can
 * collapse to icon-only with tooltips; preference persists in localStorage.
 */

/**
 * One rail row.
 *
 * Disclosure parents (items with children) are one button, not a link with a
 * separate hit area glued to it — a parent that both navigates and expands
 * makes every click a coin toss.
 */
function NavRow({
  item,
  collapsed,
  iconOnly,
  pathname,
  open,
  onToggle,
}: {
  item: NavItem
  collapsed: boolean
  /** Icon-only desktop rail — children have nowhere to render. */
  iconOnly: boolean
  pathname: string
  open: boolean
  onToggle: (id: string) => void
}) {
  const active = isNavItemActive(pathname, item)

  if (!item.children?.length) {
    return (
      <Link
        to={item.to}
        className="w7-navitem"
        title={collapsed ? `${item.label} — ${item.description}` : item.description}
        aria-label={collapsed ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
      >
        <span className="w7-navitem-icon" aria-hidden="true">
          <Icon name={item.icon} size={18} />
        </span>
        <span className="w7-navitem-label">{item.label}</span>
      </Link>
    )
  }

  const listId = `w7-nav-${item.id}`
  const expanded = open && !iconOnly

  return (
    <div className="w7-navsection" data-open={expanded ? 'true' : undefined}>
      <button
        type="button"
        className="w7-navitem w7-navsection-head"
        aria-expanded={expanded}
        aria-controls={listId}
        data-current={active ? 'true' : undefined}
        title={collapsed ? `${item.label} — ${item.description}` : item.description}
        onClick={() => onToggle(item.id)}
      >
        <span className="w7-navitem-icon" aria-hidden="true">
          <Icon name={item.icon} size={18} />
        </span>
        <span className="w7-navitem-label">{item.label}</span>
        <Icon name="chevron-down" size={14} className="w7-navsection-caret" aria-hidden="true" />
      </button>

      <div className="w7-navsub" id={listId} hidden={!expanded}>
        {item.children.map((child) => (
          <NavLink key={child.id} to={child.to} className="w7-navsub-item" end>
            <span className="w7-navsub-label">{child.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  )
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const nav = useConsoleNav()
  const [railOpen, setRailOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  // Read once during the first render: an effect that restores this would race
  // the effect that persists it, and the restore would lose.
  const [openSections, setOpenSections] = useState<string[]>(readOpenSections)
  const drawer = useIsRailDrawer()
  const iconOnly = collapsed && !drawer

  useEffect(() => {
    setCollapsed(readCollapsedPreference())
  }, [])

  // The section holding the current page is always open — an operator who deep
  // links into a ticker should see where they are, not a collapsed rail.
  const currentSection = nav
    .flatMap((group) => group.items)
    .find((item) => item.children?.length && isNavItemActive(location.pathname, item))?.id

  useEffect(() => {
    if (!currentSection) return
    setOpenSections((ids) => (ids.includes(currentSection) ? ids : [...ids, currentSection]))
  }, [currentSection])

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_OPEN_SECTIONS_KEY, JSON.stringify(openSections))
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
  }, [openSections])

  const toggleSection = (id: string) => {
    // Collapsing the rail to icons hides the children; a click on the icon is
    // asking to see them, so restore the full rail rather than doing nothing.
    if (iconOnly) {
      setCollapsed(false)
      setOpenSections((ids) => (ids.includes(id) ? ids : [...ids, id]))
      return
    }
    setOpenSections((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
  }

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
  }, [collapsed])

  // Any navigation closes the mobile rail — leaving it open over the new page
  // is the single most common phone-nav bug.
  useEffect(() => setRailOpen(false), [location.pathname])

  const toggleCollapsed = () => setCollapsed((value) => !value)

  return (
    <div className="w7-app">
      <a href="#main" className="w7-skip">
        Skip to content
      </a>

      <aside
        className="w7-rail"
        data-open={railOpen ? 'true' : undefined}
        data-collapsed={collapsed ? 'true' : undefined}
        aria-label="Console navigation"
      >
        <div className="w7-rail-head">
          <Link to="/overview" className="strix-mark-link" aria-label="Strix — overview">
            <StrixMark size={collapsed ? 28 : 26} showName={!collapsed} />
          </Link>
          <IconButton
            name="menu"
            label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="w7-rail-collapse"
            onClick={toggleCollapsed}
          />
          <IconButton
            name="close"
            label="Close menu"
            className="w7-rail-close"
            onClick={() => setRailOpen(false)}
          />
        </div>

        <nav className="w7-rail-scroll" aria-label="Console sections">
          {nav.map((group) => (
            <div key={group.id} className="w7-navgroup">
              <p className="w7-navgroup-label">{group.label}</p>
              {group.items.map((item) => (
                <NavRow
                  key={item.id}
                  item={item}
                  collapsed={collapsed}
                  iconOnly={iconOnly}
                  pathname={location.pathname}
                  open={openSections.includes(item.id)}
                  onToggle={toggleSection}
                />
              ))}
            </div>
          ))}
        </nav>

        <div
          className="w7-rail-foot"
          title={
            isDemoSession()
              ? 'Every figure here is generated, not from the ledger. Wired to live services this becomes real without a UI change.'
              : 'Figures come from hierarchy and analytics for your agentId. Demo login still uses fixtures.'
          }
        >
          <Chip
            tone={isDemoSession() ? 'warn' : 'profit'}
            size="sm"
            icon={isDemoSession() ? 'info' : 'live'}
            className="w7-rail-session"
          >
            {isDemoSession() ? 'Demo data' : 'Live session'}
          </Chip>
          <p className="w7-rail-session-hint w7-dim">
            {isDemoSession()
              ? 'Every figure here is generated, not from the ledger.'
              : 'Figures come from hierarchy and analytics for your agentId.'}
          </p>
        </div>
      </aside>

      {/* Its OWN scrim class, not the modal one. The shared `w7-scrim` sits at
          z-index 100 so it can cover a drawer at 101; the mobile rail is at 60,
          so reusing it put the scrim ON TOP of the open menu and swallowed every
          tap — the menu opened and then did nothing. */}
      {railOpen ? (
        <div className="w7-scrim w7-scrim-nav" onClick={() => setRailOpen(false)} />
      ) : null}

      <div className="w7-main">
        <Topbar onOpenRail={() => setRailOpen(true)} />
        <main className="w7-content" id="main">
          {children}
        </main>
      </div>
    </div>
  )
}

/**
 * Topbar day filter — only where the page reads `useDateRange()`.
 *
 * SHOW: Overview, Downline, Member (shell range drives their queries).
 * HIDE everywhere else: reports own a picker; exposure uses live or local filters.
 */
const SHOW_TOPBAR_RANGE = /^\/(?:overview(?:\/|$)|member(?:\/|$))/

function Topbar({ onOpenRail }: { onOpenRail: () => void }) {
  const { data: session } = useSession()
  const { range, preset, setPreset } = useDateRange()
  const { pathname } = useLocation()
  const showRange = SHOW_TOPBAR_RANGE.test(pathname)
  const { openPreferences } = useThemePrefs()

  const account = session?.account
  const demo = isDemoSession()
  const who = claims()
  const signedIn = Boolean(who || demo)
  const signedInAs =
    who?.preferredUsername || who?.email || (demo ? 'demo' : account?.username) || 'Signed in'
  const roleLabel = account ? ROLE_LABEL[account.role] : undefined

  return (
    <header className="w7-topbar">
      <IconButton name="menu" label="Open menu" className="w7-rail-toggle" onClick={onOpenRail} />

      {showRange ? (
        <div className="w7-topbar-dates">
          <Segmented
            label="Date range"
            value={preset}
            onChange={setPreset}
            items={RANGE_PRESETS.map((p) => ({ id: p.id, label: p.short }))}
          />
          <span className="w7-topbar-range w7-dim">{range.label}</span>
        </div>
      ) : null}

      {/* The palette's discoverability. Placed before the spacer so it sits with
          the workspace controls rather than the account trail — it is a way to
          get somewhere, not a setting. Collapses to the magnifier below 900px. */}
      <SearchTrigger />

      <span className="w7-spacer" />

      <div className="w7-topbar-trail">
        {signedIn || account ? (
          <Menu
            label="Account"
            align="end"
            trigger={<Avatar name={signedInAs} />}
            header={
              <div className="w7-menu-who">
                <Avatar name={signedInAs} size="sm" />
                <div className="w7-menu-who-text">
                  <span className="w7-menu-who-name w7-truncate" title={signedInAs}>
                    {signedInAs}
                  </span>
                  {roleLabel ? <span className="w7-menu-who-role">{roleLabel}</span> : null}
                </div>
              </div>
            }
            items={[
              {
                id: 'prefs',
                label: 'Appearance',
                icon: 'sliders',
                onSelect: () => openPreferences(),
              },
              'separator',
              {
                id: 'out',
                label: 'Sign out',
                icon: 'logout',
                tone: 'danger',
                onSelect: () => logout(),
              },
            ]}
          />
        ) : null}
      </div>
    </header>
  )
}

/** Identity block reused by the downline table and the member header. */
export function AccountCell({
  username,
  displayName,
  role,
  to,
  meta,
}: {
  username: string
  displayName: string
  role: string
  to?: string
  meta?: React.ReactNode
}) {
  const body = (
    <>
      <Avatar name={displayName || username} size="sm" />
      <span style={{ minWidth: 0 }}>
        <span className="w7-row" style={{ gap: 6 }}>
          <span className="w7-truncate" style={{ fontWeight: 600 }}>
            {username}
          </span>
          <RoleChip role={role} label={ROLE_LABEL[role as keyof typeof ROLE_LABEL] ?? role} />
        </span>
        <span className="w7-dim w7-truncate" style={{ display: 'block', fontSize: 11.5 }}>
          {meta ?? displayName}
        </span>
      </span>
    </>
  )

  return to ? (
    <Link to={to} className="w7-identity">
      {body}
    </Link>
  ) : (
    <span className="w7-identity">{body}</span>
  )
}

export { Button }
